/**
 * @flow
 * @format
 */
import _ from 'lodash';
import fp from 'lodash/fp';
import omit from 'lodash/omit';

import { replaceAll, isBrowserIE } from '~/utils';
import { getNextFieldsByStep, getNextFields } from '~/utils/stepHelpers';
import { Steps } from '~/utils/constants';
import HashId from '~/utils/hashid';
// import debug from '~/utils/macros/debug.macro';

import { constants, revertField } from '~/actions/design';
import type {
  DesignState,
  // DesignActions,
  DesignResponseType,
  FieldDataType,
} from '~/actions/design';

import { nextStep, prevStep } from '~/selectors/design';
import routines from '~/routines/global';
import designRoutines from '~/routines/design';
import { setIn } from '../utils';

const hashId = new HashId();

type ValT = string | number | Array<any>;
type FieldVT = {
  currentValue: ValT,
  defaultValue: ValT,
  rangeValue: { min: number, max: number },
};
type Ty = { [key: string]: FieldVT };

type APIMessageType = 'ERROR' | 'SUCCESS' | 'INFO' | null;

function buildChangedFields(fields: Ty) {
  const validValues: FieldDataType = _.pickBy(
    fields,
    (field: FieldVT): boolean =>
      !_.some([field.currentValue, field.defaultValue], _.isNull),
  );
  // $FlowIgnore
  const diff = _.pickBy(
    validValues,
    (field: FieldVT): boolean =>
      !_.isEqual(field.currentValue, field.defaultValue),
  );
  return _.mapValues(diff, () => true);
}

function fixSchematicSVG(str) {
  const props = [
    'xmlns="http://www.w3.org/2000/svg"',
    `height="${isBrowserIE() ? '' : '100%'}"`,
    `width="${isBrowserIE() ? '' : '100%'}"`,
    'viewBox="0 0 500 300"',
  ];

  // Replace any SVG attrs with our own
  return str.replace(/<svg.+(?=>)/, `<svg ${props.join(' ')}`);
}

export const initialState: DesignState = {
  loading: true,
  initialValues: {},
  name: '',
  id: null,
  currentStep: Steps.STEP_ONE,
  dirtyFields: {},
  changedFields: {},
  values: {},
  evaluating: {},
  errors: {},
  fields: {},
  info: {},
  schematic: {
    loading: false,
    hasData: false,
    data: '',
  },
  report: {
    loading: false,
    hasData: false,
    data: '',
  },
  stepStatus: {},
  openingDesign: false,
  savingDesign: false,
};

type ActionType = {
  data: any,
  payload: any,
  type: string,
};

function design(
  state: DesignState = initialState,
  action: ActionType,
): DesignState {
  switch (action.type) {
    case revertField.toString(): {
      const field: string = _.get(action, ['payload', 'field']);
      return {
        ...state,
        values: {
          ...state.values,
          [field]: {
            ...state.values[field],
            currentValue: state.values[field].defaultValue,
          },
        },
        dirtyFields: omit(state.dirtyFields, field),
        changedFields: omit(state.changedFields, field),
        errors: omit(state.errors, field),
      };
    }
    case constants.CHANGE_VALUE: {
      const field: string = _.get(action, ['payload', 'field']);
      return {
        ...state,
        values: {
          ...state.values,
          [field]: {
            ...state.values[field],
            currentValue: action.payload.value,
          },
        },
        changedFields: {
          ...state.changedFields,
          [field]: true,
        },
        errors: _.omit(state.errors, field),
        dirtyFields: {
          ...state.dirtyFields,
          [field]: state.values[field].currentValue,
        },
      };
    }

    case constants.EVALUATE: {
      const field: string = _.get(action, ['payload', 'field']);

      return {
        ...state,
        evaluating: {
          ...state.evaluating,
          [field]: true,
        },
        dirtyFields: {
          ...state.dirtyFields,
          [field]: state.values[field].currentValue,
        },
      };
    }

    case routines.fetchChartData.TRIGGER:
    case designRoutines.fetchRecord.TRIGGER:
    case constants.PENDING: {
      return {
        ...state,
        loading: true,
      };
    }

    case routines.jumpToStep.toString(): {
      return {
        ...state,
        currentStep: action.payload,
      };
    }

    case constants.AUTO_COMPLETE: {
      return {
        ...state,
        currentStep: Steps.STEP_ANALYSIS,
      };
    }

    case constants.STEP_FORWARD: {
      const next = nextStep(state.currentStep);
      return {
        ...state,
        currentStep: next,
      };
    }

    case constants.STEP_BACKWARD: {
      const prev = prevStep(state.currentStep);
      return {
        ...state,
        currentStep: prev,
      };
    }

    case constants.RENAME_DESIGN: {
      return {
        ...state,
        name: action.data,
      };
    }

    case constants.STEP_RESET: {
      // We need to reset fields values for all stages for/after this one.
      const active = state.currentStep;
      const fields = getNextFieldsByStep(active);
      return {
        ...state,
        loading: true,
        changedFields: _.omit(state.changedFields, fields),
        dirtyFields: _.omit(state.dirtyFields, fields),
        errors: _.omit(state.errors, fields),
      };
    }

    case constants.SAVE_DESIGN: {
      return {
        ...state,
        name: action.payload,
        savingDesign: true,
      };
    }

    // case constants.RECEIVE_DESIGN: {
    //   // const data: FieldDataType = action.payload.data;
    //   // const record: RecordDataType = action.payload.message.record;

    //   const {
    //     actionType,
    //     activityDttm,
    //     activityId,
    //     emailId,
    //     recordName,
    //     userId,
    //     userIpAddress,
    //     type,
    //     ...rest
    //   } = action.payload;

    //   const formatField = (str: string): string => {
    //     const fields = {
    //       ambientTmpt: 'Tamb',
    //       csHtsnkThrmlRest: 'RthCS',
    //       dcBusVolt: 'Vbus',
    //       maxHeatSinkTmpt: 'TSinkMax',
    //       maxJunctnTmpt: 'TjMax1',
    //       modulatnIdx: 'MI',
    //       motorRmsPhsCurr: 'IPhase',
    //       outputFreq: 'OutputFreq',
    //       overloadPctg: 'OverLoadPercent',
    //       partSelected: 'PartNumber',
    //       pkg: 'SpmPackage',
    //       pwmMethod: 'PWM',
    //       pwrFctr: 'PF',
    //       snkAmbThrmlRest: 'RthSA1',
    //       snkAmbThrmlRestCalcMtd: 'ManualRSink',
    //       switchingFreq: 'SwitchingFreqKhz',
    //     };

    //     return _.get(fields, str, 'UNKNOWN');
    //   };

    //   const data = _.reduce(
    //     rest,
    //     (obj: any, val: string, key: string) => {
    //       const o = obj;
    //       o[formatField(key)] = {
    //         currentValue: val,
    //         // defaultValue: val,
    //       };
    //       return o;
    //     },
    //     {},
    //   );

    //   // const diff = _.differenceWith(state.values, data, );
    //   const validSteps: FieldDataType = _.pickBy(
    //     data,
    //     (field, key: string): boolean => _.isString(getStepByField(key)),
    //   );

    //   const validValues: FieldDataType = _.pickBy(
    //     validSteps,
    //     (field: Object): boolean =>
    //       !_.some([field.currentValue, field.defaultValue], _.isNull),
    //   );

    //   const diff = _.pickBy(
    //     validValues,
    //     (field: Object): boolean =>
    //       !_.isEqual(field.currentValue, field.defaultValue),
    //   );

    //   return {
    //     ...state,
    //     values: _.merge({}, state.values, data),
    //     changedFields: _.mapValues(diff, () => true),
    //     name: recordName,
    //     loading: false,
    //   };
    // }

    case constants.NEW_DESIGN: {
      return {
        ...initialState,
        id: hashId.nextId(),
      };
    }

    case designRoutines.fetchRecord.SUCCESS:
    case constants.UPDATE: {
      // const { data, content, messageFor, message, errorSource } = action.payload;
      const payload: DesignResponseType =
        action.payload.response || action.payload;
      const emptyState = _.isEmpty(state.initialValues);

      // debug(payload);

      // type FieldValueType = {
      //   currentValue: string[] | string[][] | number | string,
      //   defaultValue: string[] | string[][] | number | string,
      //   rangeValue: { min: number, max: number },
      // };

      const data = _.reduce(
        payload.data,
        (obj: Ty, val: FieldVT, key: string) => {
          const o = obj;
          if (
            key.startsWith('Col_') ||
            key.startsWith('Desc_') ||
            key.startsWith('Numof_') ||
            key.startsWith('MaxI') ||
            key.startsWith('Manf_') ||
            key === 'BOM' ||
            key === 'Charts' ||
            key === 'CritChartData'
          ) {
            return o;
          }

          const cleanObj = fp.pick([
            'currentValue',
            'rangeValue',
            'defaultValue',
          ]);

          o[key] = cleanObj(val);

          const curVal = o[key].currentValue;

          // Fix the long tables
          if (['DiodesVIPTgood', 'FETsVIPTgood'].includes(key)) {
            const arr: string[][] = (curVal: any) || [];
            const header = _.head(arr);
            const index: number = header ? header.indexOf('Package') : -1;

            if (index > 0) {
              const fixed: string[][] = arr.map(a => _.slice(a, 0, index + 1));
              o[key].currentValue = fixed;
            }
            return o;
          }

          // clean up default / current values
          if (['DefaultCmin_tot'].includes(key)) {
            o[key].currentValue = o[key].defaultValue;
          }

          // Fix MaxPchoke currentValue
          if (['MaxPchoke'].includes(key)) {
            o[key].currentValue = o[key].rangeValue.max;
            return o;
          }

          // Append Irating_* units
          if (['Irating_HS', 'Irating_LS_dual'].includes(key)) {
            const getUnit = (index: number) => {
              if (index === 1 && key !== 'Irating_LS_dual') {
                return 'A<sub>RMS</sub>';
              }
              if (index > 0) return 'A';
              return '';
            };

            const getText = str => {
              const map = {
                max: 'Max',
                current: 'Current',
                rating: 'Rating',
                average: 'Average',
                IFav: 'I<sub>Fav</sub>',
                IDcont: 'I<sub>Dcont</sub>',
              };
              if (!str) return '';
              return replaceAll(str, map);
            };

            if (Array.isArray(curVal)) {
              const cast: Array<any> = (curVal: any);
              o[key].currentValue = cast.map((v: Array<*>, k: number) => [
                getText(v[0]),
                v[1],
                getUnit(k),
              ]);
            }

            return o;
          }

          // Some fields are missing default values
          // that need to be set on initial load.
          const FIX_DEFAULT_VALUES = [
            'Therm_opt',
            'Pkgs_Diodes',
            'Sel_diode_pkg',
            'Sel_fet_pkg',
            'Snub_sel',
            'DiodesVIPTgood',
            'FETsVIPTgood',
          ];
          if (FIX_DEFAULT_VALUES.includes(key)) {
            // This has to be checked every time or nulls get merged
            o[key].defaultValue = emptyState
              ? o[key].currentValue
              : state.initialValues[key].defaultValue;
            return o;
          }
          return o;
        },
        {},
      );

      // These fields have dependancies on other fields when setting defaults
      // so we can't use the reduce fn above to set them.
      // If the list changes, we should update the default value of the dataTarget.
      const DATA_TARGET_DEFAULT_FIX = {
        SuitableInductors: 'IndPNsel',
        Losscomp_FET: 'PN_sel_fet',
        Losscomp_diode: 'PN_sel_diode',
      };

      _.forEach(DATA_TARGET_DEFAULT_FIX, (target, key) => {
        const nextKeyValue = data[key].currentValue;
        const prevKeyValue = _.get(state.values, [key, 'currentValue'], null);
        const newDefault = _.get(nextKeyValue, ['1', '0'], null);

        // Initial state or table array has changed so we should update
        // [target].defaultValue.
        if (emptyState || !_.isEqual(prevKeyValue, nextKeyValue)) {
          data[target] = _.set(data[target], 'defaultValue', newDefault);
          return;
        }

        // A change occured somewhere else else use the last [target] object.
        data[target] = state.values[target];
      });

      const messageFor: string = _.get(payload, ['messageFor']);
      const content = _.get(payload, ['content'], {});
      const messageType: APIMessageType =
        _.get(payload, ['type']) || _.get(payload, ['message', 'type']);

      let updatedValues = data;
      // const type = get(message, 'type') || get(action.payload, 'type', 'NONE');

      // For some reason this has changed from MC and SyncBuck.. need to work around.
      const errorSource = _.get(payload, ['errorSource'], {});

      // some errors return a value that needs to be merged back into the displayed values
      if (messageType === 'ERROR' && errorSource.currentVal !== null) {
        updatedValues = _.merge(updatedValues, {
          [errorSource.key]: {
            currentValue: errorSource.currentVal,
          },
        });
      }

      const indMsg = _.get(updatedValues, ['Inductor_msg', 'currentValue']);
      let infoObject = {};

      if (indMsg) {
        infoObject = _.set(infoObject, 'IndPNsel', [indMsg]);
      }

      if (messageType === 'INFO') {
        /*
        10/5(BC): just as in Sync Buck, error message should be displayed in the
        3b  "Data for Qualified Devices" table.
        3b & 3c  "Estimated Worst Case Losses..." tables should be blank
        3c "Data for Qualified Devices..." table should be blank.
        3b packages table should remain functional.
        Also, see ref#52, inductor table in 2b should have "inductor_msg" asserted
        (no suitable parts found...).

        Vin min = Vin max = 48V
        Vout = 76V
        I out = 4A
        */

        const lossFet = _.get(updatedValues, ['Losscomp_FET', 'currentValue']);

        if (!lossFet && indMsg) {
          updatedValues = _.set(
            updatedValues,
            ['FETsVIPTgood', 'currentValue'],
            null,
          );
          updatedValues = _.set(
            updatedValues,
            ['Losscomp_diode', 'currentValue'],
            null,
          );
        }

        infoObject = _.set(infoObject, messageFor, [content]);
      }

      let errorObj = {};
      let changedObj = state.changedFields;

      if (
        messageType === 'ERROR' ||
        (messageType === 'SUCCESS' && messageFor)
      ) {
        errorObj = {
          ...errorObj,
          ...state.errors,
          [messageFor]: [content],
        };

        // We need to concat the current error fiends and the remaining fields after this one
        const remainingFields = getNextFields(messageFor);
        changedObj = omit(
          state.changedFields,
          [].concat(remainingFields, messageFor),
        );
      }

      // Loading a design record requires a rebuild of the changedFields object
      const isRecord = action.type === designRoutines.fetchRecord.SUCCESS;
      if (isRecord) {
        changedObj = buildChangedFields(updatedValues);
      }

      const lastValues = Object.assign({}, state.values);

      return {
        ...state,
        currentStep: isRecord ? 'STEP_ONE' : state.currentStep,
        loading: false,
        initialValues: emptyState ? updatedValues : state.initialValues,
        values: Object.assign({}, state.values, updatedValues),
        evaluating: {},
        dirtyFields: {},
        lastValues,
        changedFields: changedObj,
        errors: errorObj,
        info: infoObject,
        schematic: initialState.schematic,
        savingDesign: false,
        openingDesign: false,
      };
    }

    case constants.VALIDATION_ERROR: {
      const { field, message } = action.payload;

      return {
        ...state,
        errors: {
          ...state.errors,
          [field]: _.uniq(
            [].concat(state.errors[field], message).filter(i => i),
          ),
        },
        evaluating: {},
      };
    }

    case routines.fetchChartData.FULFILL:
    case designRoutines.fetchRecord.FULFILL: {
      return {
        ...state,
        loading: false,
      };
    }

    case designRoutines.copyDesign.TRIGGER: {
      if (action.payload.fromModal) return state;
      return setIn('loading', true, state);
    }

    case designRoutines.copyDesign.FULFILL: {
      return setIn('loading', false, state);
    }

    case routines.fetchSchematic.TRIGGER: {
      return {
        ...state,
        loading: true,
        schematic: {
          ...state.schematic,
          loading: true,
          hasData: false,
        },
      };
    }

    case routines.fetchSchematic.SUCCESS: {
      const svg = _.get(action.payload, ['response'], '');
      return {
        ...state,
        schematic: {
          ...state.schematic,
          hasData: true,
          data: fixSchematicSVG(svg),
        },
      };
    }

    case routines.fetchSchematic.FULFILL: {
      return {
        ...state,
        loading: false,
        schematic: {
          ...state.schematic,
          loading: false,
        },
      };
    }

    case constants.REQUEST_PDF: {
      return {
        ...state,
        report: {
          ...state.report,
          loading: true,
        },
      };
    }

    case constants.RECEIVE_PDF: {
      return {
        ...state,
        report: {
          ...state.report,
          loading: false,
          data: 'ayy',
          hasData: true,
        },
      };
    }

    default:
      return state;
  }
}

export default design;



// WEBPACK FOOTER //
// ./src/reducers/design.js